import { rhybusRListsHttp } from '../api/rhybus-list-http'
import { ListNames } from '../config/configuration'

export interface FormSchemaVersion {
    _Id?: number
    eKey?: string
    eTag?: string
    FormType: string
    Version: number
    Schema: string
    IsActive: boolean
    ChangeNote?: string
    IsArchived?: boolean
    IsDeleted?: boolean
    ItemCreated?: string
    ItemCreatedBy?: string
    ItemModified?: string
    ItemModifiedBy?: string
}

const getList = () => rhybusRListsHttp().getList(ListNames.FORM_SCHEMA_HISTORY)

// Load the current active schema for a form type
export async function loadActiveSchema(formType: string): Promise<object | null> {
    try {
        const list = await getList()
        const all = [...(await list.query().get<FormSchemaVersion>())]
        const active = all.find(r =>
            r.FormType === formType &&
            r.IsActive === true &&
            !r.IsDeleted &&
            !r.IsArchived
        )
        return active ? JSON.parse(active.Schema) : null
    } catch {
        return null
    }
}

// Load full version history for a form type
export async function loadSchemaHistory(formType: string): Promise<FormSchemaVersion[]> {
    const list = await getList()
    const all = [...(await list.query().get<FormSchemaVersion>())]
    return all
        .filter(r => r.FormType === formType && !r.IsDeleted && !r.IsArchived)
        .sort((a, b) => b.Version - a.Version)
}

// Publish new schema version — deactivates current, inserts new active version
export async function publishSchema(
    formType: string,
    schema: object,
    publishedBy: string,
    changeNote?: string
): Promise<void> {
    const list = await getList()
    const all = [...(await list.query().get<FormSchemaVersion>())]

    // Deactivate all existing active versions for this formType
    const now = new Date().toISOString()
    const activeVersions = all.filter(r => r.FormType === formType && r.IsActive === true)
    for (const v of activeVersions) {
        await list.addOrUpdateItem({ ...v, IsActive: false, ItemModified: now } as any)
    }

    // Determine next version number
    const existing = all.filter(r => r.FormType === formType)
    const nextVersion = existing.length > 0
        ? Math.max(...existing.map(r => r.Version)) + 1
        : 1

    // Insert new active version
    await list.addOrUpdateItem({
        FormType:       formType,
        Version:        nextVersion,
        Schema:         JSON.stringify(schema),
        IsActive:       true,
        ChangeNote:     changeNote || '',
        IsArchived:     false,
        IsDeleted:      false,
        ItemCreated:    now,
        ItemModified:   now,
        ItemCreatedBy:  publishedBy,
        ItemModifiedBy: publishedBy,
    } as any)
}

// Dev utility — soft-delete all versions for a form type (or all types if omitted)
export async function clearSchemaHistory(formType?: string): Promise<void> {
    const list = await getList()
    const all = [...(await list.query().get<FormSchemaVersion>())]
    const targets = formType ? all.filter(r => r.FormType === formType) : all
    const now = new Date().toISOString()
    for (const v of targets) {
        await list.addOrUpdateItem({ ...v, IsDeleted: true, IsActive: false, ItemModified: now } as any)
    }
    console.log(`[clearSchemaHistory] soft-deleted ${targets.length} record(s)`)
}

// Rollback — reactivate an existing version in place (no new version created)
export async function rollbackSchema(
    formType: string,
    targetVersion: number,
    rolledBackBy: string
): Promise<boolean> {
    try {
        const list = await getList()
        const all = [...(await list.query().get<FormSchemaVersion>())]

        const target = all.find(r => r.FormType === formType && r.Version === targetVersion)
        if (!target) return false

        const now = new Date().toISOString()

        // Deactivate all currently active versions for this form type
        const activeVersions = all.filter(r => r.FormType === formType && r.IsActive === true)
        for (const v of activeVersions) {
            await list.addOrUpdateItem({ ...v, IsActive: false, ItemModified: now } as any)
        }

        // Reactivate the target version in place
        await list.addOrUpdateItem({
            ...target,
            IsActive:       true,
            ItemModified:   now,
            ItemModifiedBy: rolledBackBy,
        } as any)

        return true
    } catch {
        return false
    }
}
